// src/pages/MyAccount.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import Layout from "../components/Layout";
import { auth, db } from "../firebase";
import { doc, getDoc, serverTimestamp, updateDoc } from "firebase/firestore";
import { sendPasswordResetEmail } from "firebase/auth";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

const COPPER = "#B87333";

function normalizePhone(input) {
  return String(input || "").replace(/[^\d]/g, "");
}

// Cache-buster helper so new photo shows immediately after upload
function withCacheBust(url, bust) {
  if (!url) return "";
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}v=${encodeURIComponent(String(bust || Date.now()))}`;
}

export default function MyAccount() {
  const user = auth.currentUser;
  const storage = getStorage();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [photoUrl, setPhotoUrl] = useState("");
  const [photoUpdatedAt, setPhotoUpdatedAt] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    displayName: "",
    phone: "",
  });

  const [status, setStatus] = useState({
    role: "",
    memberStatus: "",
    email: "",
  });

  const canSave = useMemo(() => {
    return !saving && !loading;
  }, [saving, loading]);

  /* =========================
     Load Profile Data
  ========================= */
  useEffect(() => {
    const run = async () => {
      if (!user) return;

      setLoading(true);
      try {
        const userRef = doc(db, "users", user.uid);
        const snap = await getDoc(userRef);
        const data = snap.exists() ? snap.data() : {};

        const displayName =
          data?.name || data?.displayName || user.displayName || "";

        const phone = data?.phone || data?.phoneNumber || "";

        setForm({
          displayName: String(displayName || ""),
          phone: String(phone || ""),
        });

        setPhotoUrl(data?.profilePhotoUrl || "");
        setPhotoUpdatedAt(
          data?.photoUpdatedAt?.toMillis ? data.photoUpdatedAt.toMillis() : data?.photoUpdatedAt || null
        );

        setStatus({
          role: data?.role || "",
          memberStatus: data?.status || "",
          email: user.email || data?.email || "",
        });
      } catch (err) {
        console.error("Load account failed:", err);
        alert("Could not load your account info.");
      }
      setLoading(false);
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* =========================
     Save Profile Info
  ========================= */
  const saveProfile = async () => {
    if (!user) return;

    const displayName = String(form.displayName || "").trim();
    const phoneDigits = normalizePhone(form.phone);

    if (!displayName) return alert("Please enter your name.");

    setSaving(true);
    try {
      const userRef = doc(db, "users", user.uid);

      await updateDoc(userRef, {
        name: displayName,
        phone: phoneDigits,
        nameLower: displayName.toLowerCase(),
        updatedAt: serverTimestamp(),
      });

      alert("Profile updated.");
    } catch (err) {
      console.error("Save profile failed:", err);
      alert("Could not save changes.");
    }
    setSaving(false);
  };

  /* =========================
     Upload Profile Photo
  ========================= */
  const handlePhotoUpload = async (e) => {
    if (!user) return;

    const file = e.target.files?.[0];
    if (!file) return;

    // Allow re-selecting the same file later
    if (fileInputRef.current) fileInputRef.current.value = "";

    if (!file.type.startsWith("image/")) {
      return alert("Please upload an image file.");
    }

    if (file.size > 3 * 1024 * 1024) {
      return alert("Image must be under 3MB.");
    }

    setUploadingPhoto(true);

    try {
      // ✅ Matches recommended storage rules:
      // profilePictures/<uid>/avatar.jpg
      const storageRef = ref(storage, `profilePictures/${user.uid}/avatar.jpg`);

      // Preserve content type (nice for serving/caching)
      await uploadBytes(storageRef, file, { contentType: file.type });

      const downloadURL = await getDownloadURL(storageRef);

      const userRef = doc(db, "users", user.uid);

      // Use a numeric bust value we control
      const bust = Date.now();

      await updateDoc(userRef, {
        profilePhotoUrl: downloadURL,
        photoUpdatedAt: bust,
        updatedAt: serverTimestamp(),
      });

      setPhotoUrl(downloadURL);
      setPhotoUpdatedAt(bust);

      alert("Profile photo updated.");
    } catch (err) {
      console.error("Upload failed:", err);
      alert("Could not upload image. Check Storage rules + authentication.");
    }

    setUploadingPhoto(false);
  };

  /* =========================
     Password Reset
  ========================= */
  const sendReset = async () => {
    if (!user?.email) return alert("No email found.");

    const ok = window.confirm(`Send password reset email to ${user.email}?`);
    if (!ok) return;

    try {
      await sendPasswordResetEmail(auth, user.email);
      alert("Password reset email sent.");
    } catch (err) {
      console.error("Reset email failed:", err);
      alert("Could not send reset email.");
    }
  };

  /* =========================
     UI
  ========================= */
  const photoSrc = useMemo(() => {
    return withCacheBust(photoUrl, photoUpdatedAt);
  }, [photoUrl, photoUpdatedAt]);

  return (
    <Layout>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-3xl font-bold" style={{ color: COPPER }}>
            My Account
          </h2>
          <p className="text-zinc-400 mt-2">
            Update your profile information and manage your password.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-6 text-zinc-300">
          Loading your account…
        </div>
      ) : (
        <div className="grid gap-6 max-w-2xl">
          {/* Profile Photo */}
          <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-6">
            <div className="flex items-center gap-6">
              <div className="h-28 w-28 rounded-full overflow-hidden border border-zinc-700 bg-zinc-800">
                {photoSrc ? (
                  <img
                    src={photoSrc}
                    alt="Profile"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-zinc-500 text-sm">
                    No Photo
                  </div>
                )}
              </div>

              <div className="min-w-0">
                <label className="block text-zinc-300 text-sm mb-2 font-semibold">
                  Profile Picture
                </label>

                <label
                  className={`inline-block cursor-pointer px-4 py-2 rounded font-semibold ${
                    uploadingPhoto
                      ? "bg-zinc-700 text-zinc-300 cursor-not-allowed"
                      : "bg-[#B87333] hover:bg-[#A46230] text-zinc-950"
                  }`}
                >
                  {uploadingPhoto ? "Uploading..." : "Upload Photo"}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handlePhotoUpload}
                    disabled={uploadingPhoto}
                  />
                </label>

                <p className="text-xs text-zinc-500 mt-2">
                  JPG/PNG under 3MB.
                </p>
              </div>
            </div>
          </div>

          {/* Profile Info */}
          <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-6">
            <h3 className="text-xl font-semibold text-zinc-100 mb-4">
              Profile
            </h3>

            <div className="grid gap-4">
              <div>
                <label className="block text-zinc-300 text-sm mb-1">
                  Email (login)
                </label>
                <input
                  value={status.email || ""}
                  disabled
                  className="w-full p-3 rounded bg-zinc-800 text-zinc-300 border border-zinc-700"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Email changes are disabled for stability. If you ever need an
                  email change, an admin can help.
                </p>
              </div>

              <div>
                <label className="block text-zinc-300 text-sm mb-1">Name</label>
                <input
                  value={form.displayName}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, displayName: e.target.value }))
                  }
                  className="w-full p-3 rounded bg-zinc-800 text-zinc-100 border border-zinc-700 focus:ring-2 focus:ring-[#B87333]/30"
                />
              </div>

              <div>
                <label className="block text-zinc-300 text-sm mb-1">
                  Phone (digits only)
                </label>
                <input
                  value={form.phone}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, phone: e.target.value }))
                  }
                  className="w-full p-3 rounded bg-zinc-800 text-zinc-100 border border-zinc-700 focus:ring-2 focus:ring-[#B87333]/30"
                  placeholder="e.g. 9795551234"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Stored as digits only (no formatting) for consistency.
                </p>
              </div>

              <div>
                <button
                  onClick={saveProfile}
                  disabled={!canSave}
                  className={`px-4 py-2 rounded font-semibold ${
                    canSave
                      ? "bg-[#B87333] hover:bg-[#A46230] text-zinc-950"
                      : "bg-zinc-700 text-zinc-300 cursor-not-allowed"
                  }`}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>

          {/* Membership */}
          <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-6">
            <h3 className="text-xl font-semibold text-zinc-100 mb-4">
              Membership
            </h3>

            <div className="grid gap-3 text-zinc-300">
              <div className="flex justify-between">
                <span className="text-zinc-400">Status</span>
                <span className="font-semibold text-zinc-100">
                  {status.memberStatus || "—"}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-zinc-400">Role</span>
                <span className="font-semibold text-zinc-100">
                  {status.role || "member"}
                </span>
              </div>

              <p className="text-xs text-zinc-500 pt-2">
                Membership status and role are managed by admins.
              </p>
            </div>
          </div>

          {/* Password */}
          <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-6">
            <h3 className="text-xl font-semibold text-zinc-100 mb-2">
              Password
            </h3>
            <p className="text-zinc-400 text-sm mb-4">
              Use a reset email to change your password.
            </p>

            <button
              onClick={sendReset}
              className="px-4 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-semibold border border-zinc-700"
            >
              Send Password Reset Email
            </button>
          </div>
        </div>
      )}
    </Layout>
  );
}